/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Breadcrumb, Button, Card, List, Modal, Form, Input, InputNumber, Space, Spin } from 'antd';
import { commonStyles } from '../../styles/commonStyles';
import { TableOutlined } from '@ant-design/icons';
import MainLayout from '../../components/common/MainLayout';
import { useGetFieldByIdQuery, useUpdateFieldMutation, useDeleteFieldMutation, useCreatePlotMutation, useDeletePlotMutation } from '../../api/fieldsApi';
import { toastError } from '../../utils/toast';
import { css } from '@emotion/react';
import { useSave } from '../../contexts/WorldContext';

const styles = {
  actionButtons: css({ marginBottom: 16 }),
  plotsList: css({ marginTop: 16 }),
};

const FieldPage: React.FC = () => {
  const { saveId, fieldId } = useParams<{ saveId: string; fieldId: string }>();
  const save = useSave();
  const farmId = save.farmId;
  const navigate = useNavigate();
  const { data: field, isLoading, isError, refetch } = useGetFieldByIdQuery(
    { saveId: saveId ?? '', fieldId: fieldId || '' },
    { skip: !saveId || !fieldId, refetchOnMountOrArgChange: true }
  );
  const [updateField, { isLoading: updating }] = useUpdateFieldMutation();
  const [deleteField] = useDeleteFieldMutation();
  const [createPlot, { isLoading: creatingPlot }] = useCreatePlotMutation();
  const [deletePlot] = useDeletePlotMutation();
  const [editOpen, setEditOpen] = useState(false);
  const [plotOpen, setPlotOpen] = useState(false);
  const [form] = Form.useForm();
  const [plotForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const actualFarmId = farmId || field?.farmId?.toString();
  const farmName = save.farmName || 'Сейв';

  const handleOpenEdit = () => {
    if (!field) return;
    form.setFieldsValue({
      name: field.name,
      area: field.area ?? undefined,
      location_coords: field.location_coords ?? undefined,
      description: field.description ?? undefined,
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!field) return;
    const values = await form.validateFields();
    await updateField({ saveId: saveId || '', id: String(field.id), payload: values }).unwrap();
    setEditOpen(false);
    refetch();
  };

  const handleDelete = () => {
    if (!field) return;
    modal.confirm({
      title: 'Удалить поле?',
      okText: 'Удалить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deleteField({ saveId: saveId || '', id: String(field.id) }).unwrap();
          navigate(actualFarmId ? `/saves/${saveId}` : '/');
        } catch (e) {
          toastError(e);
        }
      },
    });
  };

  const handleCreatePlot = async () => {
    if (!field) return;
    const values = await plotForm.validateFields();
    await createPlot({ saveId: saveId || '', fieldId: String(field.id), ...values }).unwrap();
    setPlotOpen(false);
    plotForm.resetFields();
    refetch();
  };

  const actionHandlers: Record<string, () => void> = {
    'fields.update': handleOpenEdit,
    'fields.delete': handleDelete,
    'plots.create': () => setPlotOpen(true),
  };

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/saves">Сейвы</Link> },
            { title: actualFarmId ? <Link to={`/saves/${saveId}`}>{farmName}</Link> : farmName },
            { title: field?.name || fieldId },
          ]}
        />
      }
    >
      {contextHolder}
      {isLoading && <Spin />}
      {isError && <div>Ошибка загрузки поля</div>}
      {field && (
        <>
          <Space css={styles.actionButtons}>
            {Array.isArray(field.actions) && field.actions.length > 0 && field.actions.map((a: any) => (
              <Button
                key={a.code}
                type={a.code === 'fields.update' ? 'primary' : 'default'}
                danger={a.state === 'danger'}
                onClick={() => actionHandlers[a.code]?.()}
              >
                {a.title}
              </Button>
            ))}
            <Button
              icon={<TableOutlined />}
              onClick={() => navigate(`/saves/${saveId}/fields/${fieldId}/plan`)}
            >
              Редактировать план
            </Button>
          </Space>
          <h2>Поле: {field.name}</h2>
          <p>Площадь: {field.area ?? '-'} га</p>
          <p>Координаты: {field.location_coords || '-'}</p>
          <p>Описание: {field.description || '-'}</p>

          {Array.isArray((field as any).plots) && (field as any).plots.length > 0 && (
            <div css={styles.plotsList}>
              <h3>Участки</h3>
              <List
                grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3 }}
                dataSource={(field as any).plots}
                renderItem={(plot: any) => (
                  <List.Item key={plot.id}>
                    <Card
                      title={plot.name}
                      hoverable
                      onClick={() => navigate(`/saves/${saveId}/plots/${plot.id}/plan`)}
                      actions={(() => {
                        const items: any[] = [];
                        if (Array.isArray(plot.actions)) {
                          plot.actions.forEach((a: any) => {
                            if (a.code === 'plots.delete') {
                              items.push(
                                <Button
                                  key="delete"
                                  danger
                                  type="link"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    modal.confirm({
                                      title: 'Удалить участок?',
                                      okText: 'Удалить',
                                      cancelText: 'Отмена',
                                      onOk: async () => {
                                        await deletePlot({ saveId: saveId || '', plotId: String(plot.id), fieldId: String(field.id) }).unwrap();
                                        refetch();
                                      },
                                    });
                                  }}
                                >
                                  {a.title || 'Удалить участок'}
                                </Button>,
                              );
                            }
                          });
                        }
                        return items.length ? items : undefined;
                      })()}
                    >
                      {'area' in plot && <p>Площадь: {plot.area ?? '-'} га</p>}
                      {'location_coords' in plot && <p>Координаты: {plot.location_coords || '-'}</p>}
                      {'description' in plot && <p>Описание: {plot.description || '-'}</p>}
                    </Card>
                  </List.Item>
                )}
              />
            </div>
          )}
        </>
      )}
      <Modal
        title="Редактировать поле"
        open={editOpen}
        onOk={handleSaveEdit}
        onCancel={() => setEditOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={updating}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Название" name="name" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Площадь (га)" name="area">
            <InputNumber css={commonStyles.fullWidth} />
          </Form.Item>
          <Form.Item label="Координаты" name="location_coords">
            <Input />
          </Form.Item>
          <Form.Item label="Описание" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Создать участок"
        open={plotOpen}
        onOk={handleCreatePlot}
        onCancel={() => setPlotOpen(false)}
        okText="Создать"
        cancelText="Отмена"
        confirmLoading={creatingPlot}
      >
        <Form form={plotForm} layout="vertical">
          <Form.Item label="Название" name="name" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Площадь (га)" name="area">
            <InputNumber css={commonStyles.fullWidth} />
          </Form.Item>
          <Form.Item label="Координаты" name="location_coords">
            <Input />
          </Form.Item>
          <Form.Item label="Описание" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default FieldPage;
